import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { initializeApp, getApps } from 'firebase/app';
import { getMessaging, getToken, isSupported } from 'firebase/messaging';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Check if Notification API is available
const isNotificationSupported = () => {
  return typeof window !== 'undefined' && 'Notification' in window;
};

// Get initial permission safely
const getInitialPermission = () => {
  if (isNotificationSupported()) {
    return Notification.permission;
  }
  return 'denied'; // Default to denied if not supported
};

// Initialize Firebase Messaging client-side safely
const getFirebaseMessaging = async () => {
  const supported = await isSupported();
  if (!supported) return null;
  const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID
  };
  const app = getApps()[0] || initializeApp(firebaseConfig);
  return getMessaging(app);
};

export const useWebNotification = () => {
  const [permission, setPermission] = useState(getInitialPermission);
  const [soundUrl, setSoundUrl] = useState(null);
  const [serviceWorkerRegistration, setServiceWorkerRegistration] = useState(null);
  const currentUser = JSON.parse(localStorage.getItem('dws_user') || '{}');

  useEffect(() => {
    loadNotificationSound();
    registerServiceWorker();
    
    if (!isNotificationSupported()) return undefined;
    if (Notification.permission !== 'default') return undefined;
    if (localStorage.getItem('notificationPermissionAsked')) return undefined;

    /**
     * The permission prompt must be triggered by a user gesture.
     *
     * This previously called requestPermission() directly on mount. Mobile
     * browsers (and Safari in particular) block or auto-deny a request made
     * without user activation, and the old code then wrote
     * notificationPermissionAsked immediately — so the denial was never retried
     * and notifications stayed permanently off on that device.
     *
     * Instead, wait for the first real interaction and ask then. The flag is only
     * persisted once the user has actually answered.
     */
    const askOnFirstGesture = async () => {
      try {
        const result = await requestPermission();
        // 'default' means the prompt never appeared; leave the flag unset so a
        // later gesture can try again.
        if (result && result !== 'default') {
          localStorage.setItem('notificationPermissionAsked', 'true');
        }
      } catch (error) {
        console.error('Notification permission request failed:', error);
      }
    };

    const handler = () => {
      cleanup();
      askOnFirstGesture();
    };
    const cleanup = () => {
      document.removeEventListener('pointerdown', handler);
      document.removeEventListener('keydown', handler);
    };

    document.addEventListener('pointerdown', handler, { once: true });
    document.addEventListener('keydown', handler, { once: true });
    return cleanup;
  }, []);


  const registerFcmToken = async (registration) => {
    try {
      if (!registration || Notification.permission !== 'granted') return;
      const messaging = await getFirebaseMessaging();
      if (!messaging) return;
      const token = await getToken(messaging, {
        vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
        serviceWorkerRegistration: registration
      });
      if (!token) return;
      const savedToken = localStorage.getItem('dws_fcm_token');
      if (savedToken === token) return;
      await axios.post(`${API}/notifications/register`, {
        token,
        deviceId: localStorage.getItem('dws_device_id') || undefined,
        platform: 'web'
      });
      localStorage.setItem('dws_fcm_token', token);
    } catch (error) {
      console.error('FCM token registration failed:', error);
    }
  };
  const registerServiceWorker = async () => {
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.register('/service-worker.js', {
          scope: '/'
        });
        setServiceWorkerRegistration(registration);
        await navigator.serviceWorker.ready;
        await registerFcmToken(registration);
      } catch (error) {
        console.error('Service Worker registration failed:', error);
      }
    }
  };

  const loadNotificationSound = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      if (response.data.notification_tune_url) {
        const url = `${BACKEND_URL}${response.data.notification_tune_url}`;
        setSoundUrl(url);
      }
    } catch (error) {
      console.error('Failed to load notification sound:', error);
    }
  };

  const requestPermission = async () => {
    if (isNotificationSupported()) {
      try {
        const perm = await Notification.requestPermission();
        setPermission(perm);
        if (perm === 'granted' && serviceWorkerRegistration) {
          await registerFcmToken(serviceWorkerRegistration);
        }
        // Only record that we asked once the user actually answered. A 'default'
        // result means the prompt was suppressed (no user gesture), and marking
        // it asked would prevent every future attempt on this device.
        if (perm !== 'default') {
          localStorage.setItem('notificationPermissionAsked', 'true');
        }
        return perm;
      } catch (error) {
        return 'denied';
      }
    }
    return 'denied';
  };

  const playFallbackBeep = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (err) {
      // silently fail
    }
  };

  const playNotificationSound = async () => {
    // Check if customer has set a custom tune (stored in localStorage)
    const customerTune = localStorage.getItem('dws_customer_tune');
    
    // None = Silent mode
    if (customerTune === 'none') return;
    
    if (customerTune && customerTune !== 'default' && customerTune !== 'none') {
      if (customerTune === 'custom') {
        const customUrl = localStorage.getItem('dws_customer_tune_url');
        if (customUrl) {
          try {
            const audio = new Audio(customUrl);
            audio.volume = 0.5;
            await audio.play();
            return;
          } catch(e) { /* fallback */ }
        }
      }
      const freqMap = { tone1: 523, tone2: 392, tone3: 587, tone4: 330 };
      const freq = freqMap[customerTune];
      if (freq) {
        try {
          const ctx = new (window.AudioContext || window.webkitAudioContext)();
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.frequency.value = freq;
          osc.type = customerTune === 'tone3' ? 'square' : customerTune === 'tone2' ? 'sine' : 'triangle';
          gain.gain.value = 0.3;
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.8);
          osc.start();
          osc.stop(ctx.currentTime + 0.8);
          return;
        } catch(e) { /* fallback */ }
      }
    }
    
    // Use admin-configured sound
    let urlToPlay = soundUrl;
    if (!urlToPlay) {
      try {
        const response = await axios.get(`${API}/settings`);
        if (response.data.notification_tune_url) {
          urlToPlay = `${BACKEND_URL}${response.data.notification_tune_url}`;
          setSoundUrl(urlToPlay);
        }
      } catch (err) { /* ignore */ }
    }
    
    if (urlToPlay) {
      try {
        let fullSoundUrl = urlToPlay;
        if (!urlToPlay.startsWith('http')) {
          const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
          if (urlToPlay.startsWith('/media/')) {
            fullSoundUrl = `${baseUrl}/api${urlToPlay}`;
          } else if (urlToPlay.startsWith('/api/')) {
            fullSoundUrl = `${baseUrl}${urlToPlay}`;
          } else {
            fullSoundUrl = `${baseUrl}/api/media/${urlToPlay.split('/').pop()}`;
          }
        }
        
        const audio = new Audio(fullSoundUrl);
        audio.volume = 0.7;
        const playPromise = audio.play();
        if (playPromise !== undefined) {
          playPromise.catch(() => playFallbackBeep());
        }
      } catch (error) {
        playFallbackBeep();
      }
    } else {
      playFallbackBeep();
    }
  };

  const showNotification = async ({ title, body, icon, tag, data }) => {
    // Push notification toggle only mutes SOUND, badges and visual notifications remain
    
    if (!isNotificationSupported() || permission !== 'granted') {
      await playNotificationSound();
      return;
    }

    try {
      await playNotificationSound();

      if (serviceWorkerRegistration && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'SHOW_NOTIFICATION',
          payload: { title, body, tag, data }
        });
      } else if (isNotificationSupported()) {
        const notificationData = data;
        const notification = new Notification(title, {
          body: body,
          tag: tag || 'dws-vault-chat',
          requireInteraction: false,
          silent: true
        });

        notification.onclick = (event) => {
          event.preventDefault();
          window.focus();
          if (notificationData && notificationData.roomId) {
            const role = currentUser.role;
            let path = '';
            if (role === 'admin') path = `/admin/chat/${notificationData.roomId}`;
            else if (role === 'handler') path = `/handler/chat/${notificationData.roomId}`;
            else if (role === 'customer') path = `/customer/chat?type=${notificationData.chatType}`;
            if (path) window.location.href = path;
          }
          notification.close();
        };

        setTimeout(() => notification.close(), 5000);
      }
    } catch (error) {
      await playNotificationSound();
    }
  };

  const updateAppBadge = async (count) => {
    try {
      if ('setAppBadge' in navigator) {
        if (count > 0) await navigator.setAppBadge(count);
        else await navigator.clearAppBadge();
      }
    } catch (err) { /* silently fail */ }
  };

  return {
    permission,
    requestPermission,
    showNotification,
    playNotificationSound,
    updateAppBadge
  };
};

